/**
 * `<echt-card-sizes>` — the desktop size quick-view on a product card.
 *
 * Basil, 7 Sep 2026, with a SET Active screenshot: "On hover can we show the
 * size quick view on desktop like this — when pressed will add one of that size
 * to the cart. Make it scrollable horizontally if it has too many sizes to fit."
 *
 * So on a hover-capable desktop the card's bottom band becomes a bar of size
 * chips. Multi-length products ask for length first, then one press on a size
 * adds that exact variant. It REPLACES stock's 36px quick-add
 * corner button there (hidden in assets/echt-bridge.css under the same media
 * gate), because the button's whole job on desktop was to open a modal that
 * asked this same question. On touch nothing here runs and nothing here is
 * styled: the corner button and its modal are untouched, which is where PRs
 * #41/#42 put the one-tap work.
 *
 * Why the element is empty and the bar is built here. Stock's
 * `snippets/card-gallery.liquid` is the only thing that can put markup inside
 * `.card-gallery`, and it is a stock file we do not edit. The card's one door is
 * a `custom-liquid` block, which renders as a SIBLING of the gallery — so the
 * payload is emitted there (snippets/echt-card-sizes.liquid) and the bar is
 * mounted into the gallery from script. Same shape as `<echt-card-video>`.
 *
 * Cost discipline, on a grid of up to 48 cards:
 *   - nothing is built until the card is first hovered or focused, so a shopper
 *     who scrolls past a card pays one `pointerenter` listener and no DOM;
 *   - no network call until a chip is pressed;
 *   - the whole component returns at connect on touch-only devices, so a phone
 *     parses the module and does nothing else with it.
 */
import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * The desktop gate. `(hover: hover) and (pointer: fine)` is a mouse — the same
 * test PR #41's touch rules use, inverted — and 750px is Horizon's own card
 * breakpoint, below which stock keeps the quick-add button permanently visible.
 * Both have to hold: an iPad at 1024px passes the width and must not get a bar
 * whose only affordance is hover.
 */
const DESKTOP = '(hover: hover) and (pointer: fine) and (min-width: 750px)';

/**
 * What counts as "the card", and as "the image box inside it".
 *
 * The first two entries in each are the collection grid and the homepage
 * product rows — the original, unchanged targets. The third pair is the PDP's
 * "Pair with" tile (blocks/echt-style-with.liquid), added 8 Sep 2026 on Basil's
 * "make hovering over these showcase the size selector as well like everywhere
 * else". A tile is a card in every way this component cares about: a product
 * image, a box that ends at the bottom of it, hovered as a unit.
 *
 * Generalising the two selectors rather than forking the component, because a
 * fork would be a second copy of the add path, the chip states and the drawer
 * contract to keep in sync. Card behaviour is byte-identical: a
 * `.product-grid__item` still resolves to itself and still mounts into its own
 * `.card-gallery`, because `closest()` and `querySelector()` both take the
 * NEAREST match and no card contains a Pair with tile or the reverse.
 */
const CARD = '.product-grid__item, .resource-list__item, .echt-pair-with__tile';
const GALLERY = '.card-gallery, .echt-pair-with__media';

/** How long a chip holds its "added" state before returning to rest. */
const ADDED_MS = 1600;

/** How long an inline error stays up. Long enough to read a Shopify message. */
const ERROR_MS = 6000;

class EchtCardSizes extends Component {
  /** @type {HTMLElement | null} */
  #card = null;

  /** @type {HTMLElement | null} */
  #gallery = null;

  /** @type {HTMLElement | null} */
  #bar = null;

  /** @type {HTMLButtonElement | null} */
  #lengthButton = null;

  /** @type {string[]} */
  #lengths = [];

  /** @type {HTMLElement | null} */
  #error = null;

  /** @type {Array<{id: number, label: string, name: string, available: boolean, length: string}>} */
  #variants = [];

  /** One add at a time per card, so a double press cannot post twice. */
  #busy = false;

  #controller = new AbortController();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTimer;

  connectedCallback() {
    super.connectedCallback();

    // Media query, not a width read: a window resized across 750px, or a
    // desktop with a touchscreen, gets the right answer without a resize
    // listener, because the CSS is gated on the same query and simply stops
    // painting the bar. The JS only has to avoid BUILDING it where it can never
    // be reached.
    if (!window.matchMedia(DESKTOP).matches) return;

    try {
      this.#variants = JSON.parse(this.dataset.variants || '[]');
    } catch {
      return;
    }
    if (this.#variants.length < 2) return;
    this.#lengths = [...new Set(this.#variants.map((variant) => variant.length).filter(Boolean))];

    // The card wrappers every other card rule in this project scopes to, plus
    // the PDP's Pair with tile — see CARD / GALLERY above for why they are one
    // list rather than two components.
    this.#card = /** @type {HTMLElement | null} */ (this.closest(CARD));
    this.#gallery = /** @type {HTMLElement | null} */ (this.#card?.querySelector(GALLERY) ?? null);
    if (!this.#gallery) return;

    // The flag the CSS hangs off. It is set HERE, not after the first build, so
    // that hiding the stock corner button happens before the first hover rather
    // than during it — a button that appears and then vanishes under the
    // pointer is worse than one that was never there. Everything the build
    // needs is already resolved above, so a card that carries this attribute
    // can always produce a bar.
    this.dataset.ready = '';

    const { signal } = this.#controller;
    const build = () => this.#build();
    this.#card?.addEventListener('pointerenter', build, { once: true, signal });
    this.#card?.addEventListener('focusin', build, { once: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    clearTimeout(this.#resetTimer);
  }

  /** Builds the bar once, on first hover or first keyboard entry into the card. */
  #build() {
    if (this.#bar || !this.#gallery) return;

    const wrap = document.createElement('div');
    wrap.className = 'echt-card-sizes';

    const bar = document.createElement('div');
    bar.className = 'echt-card-sizes__bar';
    // Each step is a group of buttons, named for its action and product.
    bar.setAttribute('role', 'group');
    bar.setAttribute('aria-label', `Add a size — ${this.dataset.productTitle || 'product'}`);

    // The error line lives above the bar, not inside it: the bar scrolls
    // sideways, and a message a shopper has to scroll to find is not a message.
    const error = document.createElement('p');
    error.className = 'echt-card-sizes__error';
    error.setAttribute('role', 'status');
    error.hidden = true;

    const lengthButton = document.createElement('button');
    lengthButton.type = 'button';
    lengthButton.className = 'echt-card-sizes__chip echt-card-sizes__back';
    lengthButton.hidden = true;
    lengthButton.addEventListener('mousedown', (event) => event.stopPropagation());
    lengthButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.#busy) return;
      this.#showLengths(true);
    });

    wrap.append(error, lengthButton, bar);
    this.#gallery.append(wrap);
    this.#bar = bar;
    this.#error = error;
    this.#lengthButton = lengthButton;
    if (this.#lengths.length > 1) this.#showLengths();
    else this.#showSizes();
  }

  /** Show the length step without navigating the card or adding to cart. */
  #showLengths(focus = false) {
    if (!this.#bar || !this.#lengthButton) return;
    this.#hideError();
    this.#lengthButton.hidden = true;
    this.#bar.replaceChildren();
    this.#bar.scrollLeft = 0;
    this.#bar.setAttribute('aria-label', `Choose length — ${this.dataset.productTitle || 'product'}`);
    for (const length of this.#lengths) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'echt-card-sizes__chip';
      button.textContent = length;
      button.addEventListener('mousedown', (event) => event.stopPropagation());
      button.disabled = !this.#variants.some((variant) => variant.length === length && variant.available);
      button.setAttribute('aria-label', button.disabled ? `${length} — sold out` : `Choose ${length}`);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#showSizes(length, true);
      });
      this.#bar.append(button);
    }
    if (focus) this.#bar.querySelector('button:not(:disabled)')?.focus({ preventScroll: true });
  }

  /** @param {string} [length] The shopper's chosen length. */
  #showSizes(length, focus = false) {
    if (!this.#bar || !this.#lengthButton) return;
    this.#bar.replaceChildren();
    this.#bar.scrollLeft = 0;
    this.#bar.setAttribute('aria-label', `Add a size${length ? ` / ${length}` : ''} — ${this.dataset.productTitle || 'product'}`);
    if (this.#lengths.length > 1 && length) {
      this.#lengthButton.hidden = false;
      this.#lengthButton.textContent = `← ${length}`;
      this.#lengthButton.setAttribute('aria-label', `Change length — ${length} selected`);
    }
    for (const variant of this.#variants) {
      if (length && variant.length !== length) continue;
      this.#bar.append(this.#chip(variant));
    }
    // Keep keyboard focus inside the selector when its length button disappears.
    if (focus) this.#bar.querySelector('button:not(:disabled)')?.focus();
  }

  /**
   * @param {{id: number, label: string, name: string, available: boolean, length: string}} variant - One buyable size.
   * @returns {HTMLButtonElement} The chip.
   */
  #chip(variant) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'echt-card-sizes__chip add-to-cart-button';
    const label = document.createElement('span');
    label.className = 'add-to-cart-text';
    label.textContent = variant.length && this.#lengths.length === 1 ? `${variant.label} / ${variant.length}` : variant.label;
    chip.append(label);
    const feedback = this.querySelector('template[data-add-feedback]');
    if (feedback instanceof HTMLTemplateElement) chip.append(feedback.content.cloneNode(true));
    chip.dataset.variantId = String(variant.id);
    // Only a button press should bypass the carousel's mouse-drag starter.
    // Keep pointerdown bubbling: slideshow uses it to finish previous gestures.
    chip.addEventListener('mousedown', (event) => event.stopPropagation());

    // The long option value is the accessible name and the tooltip, because
    // "XXS" read aloud is not a size. The length is appended only where the
    // product has one, so the eleven-in-twelve single-axis products stay terse.
    const full = variant.length ? `${variant.name} / ${variant.length}` : variant.name;

    if (!variant.available) {
      // Disabled, not hidden: the row of sizes is itself information, and a
      // shopper reading "M is gone" from the card is a shopper who does not
      // open the product to find out.
      chip.disabled = true;
      chip.setAttribute('aria-label', `${full} — sold out`);
      chip.title = `${full} — sold out`;
      return chip;
    }

    chip.setAttribute('aria-label', `Add ${full}`);
    chip.title = full;
    chip.addEventListener('click', (event) => {
      // The card is a link (`<product-card>` binds click -> navigate, and
      // `.product-card__link` is stretched over it), so a chip press must not
      // also be a press on the card.
      event.preventDefault();
      event.stopPropagation();
      this.#add(chip, variant);
    });

    return chip;
  }

  /**
   * Adds one of a size, then hands the drawer the same event a PDP add hands it.
   *
   * @param {HTMLButtonElement} chip - The pressed chip.
   * @param {{id: number, label: string, name: string, length: string}} variant - What to add.
   * @returns {Promise<void>}
   */
  async #add(chip, variant) {
    if (this.#busy) return;
    this.#busy = true;
    clearTimeout(this.#resetTimer);
    this.#hideError();
    this.#bar?.querySelectorAll('[data-added]').forEach((previous) => {
      previous.removeAttribute('data-added');
      previous.classList.remove('is-added', 'is-error');
    });
    delete chip.dataset.added;
    chip.classList.add('is-adding');
    chip.setAttribute('aria-busy', 'true');

    // The cart drawer re-renders itself from the sections the add response
    // carries, exactly as a PDP add does (assets/product-form.js:429-436 and
    // assets/component-cart-items.js:88-91). Without these ids the drawer would
    // open showing the cart as it was before this add.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : null))
      .filter(Boolean);

    const body = new FormData();
    body.set('id', String(variant.id));
    body.set('quantity', '1');
    if (sectionIds.length) {
      body.set('sections', sectionIds.join(','));
      body.set('sections_url', window.location.pathname);
    }

    // Dispatched BEFORE the request, with a promise the listeners await — this
    // is the contract `<echt-cart-drawer>` reads (#onCartLinesUpdate wants
    // `action === 'add'` and `event.promise`, and skips on `detail.didError`),
    // and it is what makes the drawer open by itself here as it does from the
    // PDP. `context: 'product'` because this is a product add, not a cart edit.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body });
      const response = await fetch(Theme.routes.cart_add_url, config);
      const payload = await response.json();

      // Shopify answers a rejected add with 4xx and a `status` field — sold out
      // between render and press, or a stock limit already in the bag.
      const failed = Boolean(payload?.status);

      // One read of /cart.js either way, like the drawer's own write path and
      // like stock's form: the add response is the LINE, and every listener
      // (the cart icon's count, the drawer's gift threshold) needs the whole
      // cart — including after a rejected add, where the cart still moved for
      // an earlier press.
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      if (!failed) {
        chip.removeAttribute('aria-busy');
        chip.dataset.added = 'true';
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: failed ? 0 : 1,
          sections: failed ? undefined : payload.sections,
          source: 'echt-card-sizes',
          didError: failed,
        },
      });

      if (failed) {
        delete chip.dataset.added;
        const message = payload.description || payload.message || 'Could not add';
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        this.#showError(message);
        chip.classList.add('is-error');
        return;
      }

      chip.classList.add('is-added');
    } catch (error) {
      delete chip.dataset.added;
      deferred.reject(error);
      this.#showError('Could not add. Try again.');
      chip.classList.add('is-error');
      console.warn('[echt-card-sizes] Add failed:', error);
    } finally {
      chip.classList.remove('is-adding');
      chip.removeAttribute('aria-busy');
      this.#busy = false;
      this.#resetTimer = setTimeout(() => {
        chip.classList.remove('is-added', 'is-error');
        delete chip.dataset.added;
      }, ADDED_MS);
    }
  }

  /** @param {string} message - What went wrong, in Shopify's own words where it gave any. */
  #showError(message) {
    if (!this.#error) return;
    this.#error.textContent = message;
    this.#error.hidden = false;
    setTimeout(() => this.#hideError(), ERROR_MS);
  }

  #hideError() {
    if (!this.#error) return;
    this.#error.hidden = true;
    this.#error.textContent = '';
  }
}

if (!customElements.get('echt-card-sizes')) {
  customElements.define('echt-card-sizes', EchtCardSizes);
}
